import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Layers, Tag, Ruler, Package } from 'lucide-react';
import { SearchInput } from '@/shared/ui/SearchInput';
import { ServicesTable } from './tables/ServicesTable';
import { CategoriesTable } from './tables/CategoriesTable';
import { UnitsTable } from './tables/UnitsTable';
import { RecipesTable } from './tables/RecipesTable';
import { Service, ServiceCategory as Category, Unit, Recipe } from '@/shared/model';
import { RecipeCategoryMap } from '@/stores';

interface CatalogViewProps {
    services: Service[];
    categories: Category[];
    units: Unit[];
    recipes: RecipeCategoryMap;
    updateService: (id: string, field: string, value: string | number | boolean) => void;
    deleteService: (id: string) => void;
    updateCategory: (id: string, field: keyof Category, value: string | number | boolean) => void;
    deleteCategory: (id: string) => void;
    updateUnit: (id: string, name: string) => void;
    deleteUnit: (id: string) => void;
    onEditRecipe: (recipe: Recipe, category: string) => void;
    onDeleteRecipe: (id: string, category: string) => void;
    onCreateService: () => void;
    onCreateCategory: () => void;
    onCreateUnit: () => void;
    onCreateRecipe: (category: string) => void;
}

export const CatalogView: FC<CatalogViewProps> = ({
    services,
    categories,
    units,
    recipes,
    updateService,
    deleteService,
    updateCategory,
    deleteCategory,
    updateUnit,
    deleteUnit,
    onEditRecipe,
    onDeleteRecipe,
    onCreateService,
    onCreateCategory,
    onCreateUnit,
    onCreateRecipe
}) => {
    const { t } = useTranslation();
    const [subTab, setSubTab] = useState<'services' | 'categories' | 'units' | 'recipes'>('services');
    const [search, setSearch] = useState('');

    const filteredServices = services.filter(s => 
        !search || s.name.toLowerCase().includes(search.toLowerCase())
    );

    const filteredCategories = categories.filter(c =>
        !search || c.name.toLowerCase().includes(search.toLowerCase())
    );

    const filteredUnits = units.filter(u =>
        !search || u.name.toLowerCase().includes(search.toLowerCase())
    );

    // For recipes, we need to filter the nested lists
    const filteredRecipes: RecipeCategoryMap = Object.entries(recipes).reduce((acc, [cat, list]) => {
        const matched = list.filter(r => 
            !search || r.name.toLowerCase().includes(search.toLowerCase())
        );
        if (matched.length > 0 || !search) acc[cat] = matched;
        return acc;
    }, {} as RecipeCategoryMap);

    const handleCreate = () => {
        switch (subTab) {
            case 'services':
                onCreateService();
                break;
            case 'recipes':
                onCreateRecipe('wall');
                break;
            case 'categories':
                onCreateCategory();
                break;
            case 'units':
                onCreateUnit();
                break;
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-blue-50/60 dark:bg-slate-900/60 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 dark:border-slate-800 shadow-sm transition-all">
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl overflow-x-auto max-w-full">
                    <button 
                        onClick={() => setSubTab('services')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${subTab === 'services' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-300 shadow-sm' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    >
                        <Tag size={14}/> {t('catalog.tabs.services', 'УСЛУГИ')}
                    </button>
                    <button 
                        onClick={() => setSubTab('recipes')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${subTab === 'recipes' ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    >
                        <Layers size={14}/> {t('catalog.tabs.recipes', 'ШАБЛОНЫ')}
                    </button>
                    <button 
                        onClick={() => setSubTab('categories')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${subTab === 'categories' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-300 shadow-sm' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    >
                        <Package size={14}/> {t('catalog.tabs.categories', 'КАТЕГОРИИ')}
                    </button>
                    <button 
                        onClick={() => setSubTab('units')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${subTab === 'units' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-300 shadow-sm' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    >
                        <Ruler size={14}/> {t('catalog.tabs.units', 'ЕДИНИЦЫ')}
                    </button>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <SearchInput 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClear={() => setSearch('')}
                        placeholder={t('catalog.search_placeholder', 'Поиск...')}
                        className="md:w-64"
                    />
                    
                    {/* Явные кнопки создания в зависимости от контекста, чтобы избежать ошибок */}
                    <button 
                        onClick={handleCreate}
                        className={`flex items-center gap-2 px-4 py-2.5 text-white rounded-xl shadow-lg active:scale-95 transition-all font-bold text-sm ${subTab === 'recipes' ? 'bg-purple-600 shadow-purple-200 dark:shadow-none hover:bg-purple-700' : 'bg-blue-600 shadow-blue-200 dark:shadow-none hover:bg-blue-700'}`}
                    >
                        <Plus size={18}/>
                        <span className="hidden sm:inline">
                            {subTab === 'services' && t('catalog.add_service_btn', 'Добавить услугу')}
                            {subTab === 'recipes' && t('catalog.add_recipe_btn', 'Создать шаблон')}
                            {subTab === 'categories' && t('catalog.add_category_btn', 'Категория')}
                            {subTab === 'units' && t('catalog.add_unit_btn', 'Единица')}
                        </span>
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden transition-colors">
                {subTab === 'services' && (
                    <ServicesTable 
                        services={filteredServices} 
                        categories={categories} 
                        units={units} 
                        onUpdate={updateService} 
                        onDelete={deleteService}
                    />
                )}
                {subTab === 'recipes' && (
                    <RecipesTable 
                        recipes={filteredRecipes}
                        onEdit={onEditRecipe}
                        onDelete={onDeleteRecipe}
                    />
                )}
                {subTab === 'categories' && (
                    <CategoriesTable 
                        categories={filteredCategories} 
                        onUpdate={updateCategory} 
                        onDelete={deleteCategory}
                    />
                )}
                {subTab === 'units' && (
                    <UnitsTable 
                        units={filteredUnits} 
                        onUpdate={updateUnit} 
                        onDelete={deleteUnit}
                    />
                )}
            </div>
        </div>
    );
};
